/**
 * B146 — the device-pairing credential as ONE copyable line (§9.1).
 *
 * device.enroll delivers the credential secret EXACTLY ONCE, on the
 * admin's enrolled screen (B20a). The hand-off to the device must not
 * decay into five hand-typed fields with an "epoch ms" box — an
 * operator never types ids or machine time (FORMS.md R7/R9). This
 * codec is the contract between the two surfaces: the admin ENCODES
 * the whole credential into one line, the validator PARSES it, and a
 * damaged paste refuses with a named reason instead of becoming a
 * half-credential.
 *
 * Base64url over canonical JSON — opaque enough that nobody edits one
 * field by hand, plain enough to audit. NOT encryption: the line
 * travels the same trusted screen-to-device channel the bare secret
 * always did, and custody rules are unchanged (digest-only server
 * side; losing it means revoke + re-enroll).
 */

/** What the admin's enrolled screen hands the device — §9.1 whole. */
export interface PairingCredential {
  readonly tenantId: string;
  readonly deviceId: string;
  readonly credentialId: string;
  readonly secret: string;
  /** Machine time (epoch ms) — rides INSIDE the line, never typed. */
  readonly expiresAt: number;
}

/** The line's self-identifying head — a paste either is one or is not. */
export const PAIRING_CREDENTIAL_PREFIX = "TRANSACT-DEVICE-V1.";

export type PairingParseFailure =
  /** Nothing pasted yet — not an error face, just an unarmed form. */
  | "empty"
  /** The text is not a pairing credential at all (wrong or missing head). */
  | "not_a_pairing_credential"
  /** The head is right but the rest is torn — recopy the WHOLE line. */
  | "damaged";

export type PairingParseResult =
  | { readonly ok: true; readonly credential: PairingCredential }
  | { readonly ok: false; readonly reason: PairingParseFailure };

const STRING_FIELDS = ["tenantId", "deviceId", "credentialId", "secret"] as const;

function validCredential(value: unknown): PairingCredential | undefined {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return undefined;
  const record = value as Record<string, unknown>;
  for (const field of STRING_FIELDS) {
    const v = record[field];
    if (typeof v !== "string" || v.length === 0) return undefined;
  }
  const expiresAt = record["expiresAt"];
  if (typeof expiresAt !== "number" || !Number.isSafeInteger(expiresAt) || expiresAt < 0) return undefined;
  return Object.freeze({
    tenantId: record["tenantId"] as string,
    deviceId: record["deviceId"] as string,
    credentialId: record["credentialId"] as string,
    secret: record["secret"] as string,
    expiresAt,
  });
}

const B64URL = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_";
const B64URL_INDEX: ReadonlyMap<string, number> = new Map([...B64URL].map((c, i) => [c, i]));

function toBase64Url(bytes: Uint8Array): string {
  let out = "";
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i] as number;
    const b = bytes[i + 1];
    const c = bytes[i + 2];
    out += B64URL[a >> 2] as string;
    out += B64URL[((a & 0x03) << 4) | ((b ?? 0) >> 4)] as string;
    if (b === undefined) break;
    out += B64URL[((b & 0x0f) << 2) | ((c ?? 0) >> 6)] as string;
    if (c === undefined) break;
    out += B64URL[c & 0x3f] as string;
  }
  return out;
}

function fromBase64Url(text: string): Uint8Array | undefined {
  if (text.length === 0 || text.length % 4 === 1) return undefined;
  const bytes: number[] = [];
  let buffer = 0;
  let bits = 0;
  for (const char of text) {
    const value = B64URL_INDEX.get(char);
    if (value === undefined) return undefined;
    buffer = (buffer << 6) | value;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      bytes.push((buffer >> bits) & 0xff);
    }
  }
  // leftover bits must be zero padding — anything else is a torn tail
  if ((buffer & ((1 << bits) - 1)) !== 0) return undefined;
  return Uint8Array.from(bytes);
}

/** Admin side: the whole credential as one copyable line. Refuses garbage input loudly. */
export function encodePairingCredential(credential: PairingCredential): string {
  if (validCredential(credential) === undefined) {
    throw new Error("encodePairingCredential: refusing to encode an invalid credential");
  }
  const canonical = JSON.stringify({
    tenantId: credential.tenantId,
    deviceId: credential.deviceId,
    credentialId: credential.credentialId,
    secret: credential.secret,
    expiresAt: credential.expiresAt,
  });
  return `${PAIRING_CREDENTIAL_PREFIX}${toBase64Url(new TextEncoder().encode(canonical))}`;
}

/**
 * Device side: parse a paste. Whitespace anywhere is tolerated (real
 * pastes wrap and pick up newlines); everything else refuses.
 */
export function parsePairingCredential(pasted: string): PairingParseResult {
  const line = pasted.replace(/\s+/g, "");
  if (line.length === 0) return { ok: false, reason: "empty" };
  if (!line.startsWith(PAIRING_CREDENTIAL_PREFIX)) return { ok: false, reason: "not_a_pairing_credential" };
  const bytes = fromBase64Url(line.slice(PAIRING_CREDENTIAL_PREFIX.length));
  if (bytes === undefined) return { ok: false, reason: "damaged" };
  let decoded: unknown;
  try {
    decoded = JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(bytes));
  } catch {
    return { ok: false, reason: "damaged" };
  }
  const credential = validCredential(decoded);
  if (credential === undefined) return { ok: false, reason: "damaged" };
  return { ok: true, credential };
}
